import React, { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { ChatBubble } from './ChatBrand';
import likeIcon from '../../styles/assets/img/chat/like.svg';
import dislikeIcon from '../../styles/assets/img/chat/dislike.svg';
import PreviewModal from '../PreviewModal';

const PdfFallbackViewer = lazy(() => import('../PdfFallbackViewer'));

// Configure marked for inline-friendly output
marked.setOptions({ breaks: true, gfm: true });

/**
 * Renders a single chat message (user or assistant).
 * Assistant messages support full markdown; user messages are plain text.
 * Assistant messages may include sell sheet action buttons (preview/download).
 * When onFeedback is passed, the assistant message shows like/dislike buttons.
 */
export default function ChatMessage({ message, onFeedback }) {
    const isUser = message.role === 'user';

    const html = useMemo(() => {
        if (isUser || !message.content) return null;
        const raw = marked.parse(message.content);
        return DOMPurify.sanitize(raw);
    }, [isUser, message.content]);

    const apiUrl = process.env.REACT_APP_API_URL || '/api/';
    const sellSheet = message.sellSheet;
    const [showPreview, setShowPreview] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [previewError, setPreviewError] = useState('');

    useEffect(() => {
        if (isUser || (!sellSheet?.id && !sellSheet?.pdfBase64)) return;
        const controller = new AbortController();
        let objectUrl;
        setPdfUrl(null);
        setPreviewError('');
        const loadPreview = async () => {
            try {
                let blob;
                if (sellSheet.pdfBase64) {
                    const bytes = Uint8Array.from(atob(sellSheet.pdfBase64), character => character.charCodeAt(0));
                    blob = new Blob([bytes], { type: 'application/pdf' });
                } else {
                    const response = await fetch(`${apiUrl}SellSheet/Download/${sellSheet.id}?inline=true`, {
                        credentials: 'include', signal: controller.signal,
                    });
                    if (!response.ok) throw new Error(`Preview returned HTTP ${response.status}.`);
                    blob = await response.blob();
                }
                if (controller.signal.aborted) return;
                objectUrl = URL.createObjectURL(blob);
                setPdfUrl(objectUrl);
            } catch (error) {
                if (!controller.signal.aborted) {
                    setPreviewError(error.message || 'Could not load the preview.');
                    setShowPreview(false);
                }
            }
        };
        loadPreview();
        return () => {
            controller.abort();
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [isUser, sellSheet?.id, sellSheet?.pdfBase64, apiUrl]);

    return (
        <div className={`chat-message-row ${isUser ? 'chat-message-row-user' : 'chat-message-row-bot'}`}>
            <ChatBubble from={isUser ? 'user' : 'bot'}>
                {isUser ? (
                    <div className="chat-bubble-text">{message.content}</div>
                ) : (
                    <>
                        <div
                            className="chat-markdown"
                            dangerouslySetInnerHTML={{ __html: html || '' }}
                        />
                        {sellSheet && (
                            <div className="chat-sellsheet-container">
                                {previewError && <div role="alert">{previewError}</div>}
                                <div role="region" aria-label="Sell sheet inline preview"
                                    style={{ width: '100%', height: 360, minWidth: 0 }}>
                                    {pdfUrl ? (
                                        <Suspense fallback={<div role="status">Loading preview...</div>}>
                                            <PdfFallbackViewer document={pdfUrl} />
                                        </Suspense>
                                    ) : !previewError && <div role="status">Loading preview...</div>}
                                </div>
                                <div className="chat-sellsheet-actions">
                                    <button
                                        type="button"
                                        disabled={!pdfUrl}
                                        onClick={() => setShowPreview(true)}
                                        className="chat-sellsheet-btn chat-sellsheet-preview-btn"
                                    >
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                            <circle cx="12" cy="12" r="3" />
                                        </svg>
                                        Open Full Preview
                                    </button>
                                    <a
                                        href={sellSheet.id ? `${apiUrl}SellSheet/Download/${sellSheet.id}` : pdfUrl || undefined}
                                        download={sellSheet.fileName || `sellsheet-${sellSheet.id}.pdf`}
                                        className="chat-sellsheet-btn chat-sellsheet-download-btn"
                                    >
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                            <polyline points="7 10 12 15 17 10" />
                                            <line x1="12" y1="15" x2="12" y2="3" />
                                        </svg>
                                        Download
                                    </a>
                                </div>
                                {showPreview && (
                                    <PreviewModal
                                        show={showPreview}
                                        setShow={setShowPreview}
                                        isLoading={!pdfUrl}
                                        pdfUrl={pdfUrl || undefined}
                                        pdfFileName={sellSheet.fileName || `sellsheet-${sellSheet.id}.pdf`}
                                        showGenerateBack={false}
                                        showPrintDownload={false}
                                        supportsProfessionalPrinting={false}
                                        showProviderTerritory={false}
                                        className="chat-sellsheet-modal"
                                        backdropClassName="chat-sellsheet-backdrop"
                                        pdfViewerFallback
                                        skipPreviewTrack
                                    />
                                )}
                            </div>
                        )}
                    </>
                )}
            </ChatBubble>
            {!isUser && onFeedback && (
                <div className={`chat-feedback${message.feedback ? ' chat-feedback-rated' : ''}`}>
                    <button
                        type="button"
                        className={`chat-feedback-btn${message.feedback === 'like' ? ' chat-feedback-btn-selected' : ''}`}
                        onClick={() => onFeedback(message.id, 'like')}
                        aria-label="Helpful answer"
                        aria-pressed={message.feedback === 'like'}
                        title="Helpful"
                    >
                        <img src={likeIcon} alt="" />
                    </button>
                    <button
                        type="button"
                        className={`chat-feedback-btn${message.feedback === 'dislike' ? ' chat-feedback-btn-selected' : ''}`}
                        onClick={() => onFeedback(message.id, 'dislike')}
                        aria-label="Unhelpful answer"
                        aria-pressed={message.feedback === 'dislike'}
                        title="Not helpful"
                    >
                        <img src={dislikeIcon} alt="" />
                    </button>
                </div>
            )}
        </div>
    );
}
